import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CaseStudyCard = ({ caseStudy, onViewDetails }) => {
  const getIndustryIcon = (industry) => {
    const iconMap = {
      'Healthcare': 'Heart',
      'Financial Services': 'Building2',
      'Manufacturing': 'Factory',
      'Retail': 'ShoppingCart',
      'Technology': 'Cpu',
      'Education': 'GraduationCap'
    };
    return iconMap?.[industry] || 'Building';
  };

  const getCompanySizeColor = (size) => {
    const colorMap = {
      'Enterprise': 'bg-purple-100 text-purple-800',
      'Mid-Market': 'bg-blue-100 text-blue-800',
      'SMB': 'bg-green-100 text-green-800'
    };
    return colorMap?.[size] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-xl shadow-executive border border-gray-200 overflow-hidden hover:shadow-executive-lg transition-all duration-300 group">
      {/* Header Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={caseStudy?.image}
          alt={`${caseStudy?.company} case study`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon 
                name={getIndustryIcon(caseStudy?.industry)} 
                size={16} 
                className="text-white" 
              />
              <span className="text-white text-sm font-medium">{caseStudy?.industry}</span>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCompanySizeColor(caseStudy?.companySize)}`}>
              {caseStudy?.companySize}
            </span>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-bold text-text-primary group-hover:text-brand-primary transition-colors duration-200">
            {caseStudy?.company}
          </h3>
          <div className="flex items-center space-x-1 text-sm text-text-secondary">
            <Icon name="Clock" size={14} />
            <span>{caseStudy?.timeline}</span>
          </div>
        </div>

        <p className="text-text-secondary mb-4 line-clamp-3">
          {caseStudy?.challenge}
        </p>

        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {caseStudy?.keyMetrics?.map((metric, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl font-bold text-brand-primary mb-1">
                {metric?.value}
              </div>
              <div className="text-xs text-text-secondary">
                {metric?.label}
              </div>
            </div>
          ))}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {caseStudy?.tags?.slice(0, 3)?.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
            >
              {tag}
            </span>
          ))}
          {caseStudy?.tags?.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
              +{caseStudy?.tags?.length - 3} more
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            variant="default"
            size="sm"
            className="flex-1 bg-brand-primary hover:bg-brand-primary/90"
            iconName="Eye"
            iconPosition="left"
            onClick={() => onViewDetails(caseStudy)}
          >
            View Details
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Download
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyCard;